"use client";

import React from "react";
import { Phone, Clock, MapPin, ShieldCheck, Navigation, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const coverageLocations = [{
  name: "Jonesboro",
  zip: "30236, 30238",
  estTime: "15-25 min"
}, {
  name: "Morrow",
  zip: "30260",
  estTime: "20-30 min"
}, {
  name: "Riverdale",
  zip: "30274, 30296",
  estTime: "20-35 min"
}, {
  name: "Forest Park",
  zip: "30297",
  estTime: "25-35 min"
}, {
  name: "Lovejoy",
  zip: "30250",
  estTime: "20-30 min"
}, {
  name: "Stockbridge",
  zip: "30281",
  estTime: "25-40 min"
}, {
  name: "Rex",
  zip: "30273",
  estTime: "20-35 min"
}, {
  name: "Lake City",
  zip: "30260",
  estTime: "20-30 min"
}, {
  name: "Fayetteville",
  zip: "30214, 30215",
  estTime: "25-40 min"
}, {
  name: "Hampton",
  zip: "30228",
  estTime: "25-40 min"
}];
const highlights = [{
  title: "24/7 Immediate Dispatch",
  description: "Fully-equipped mobile plumbing units on call day, night, weekends, and holidays."
}, {
  title: "Upfront Pricing",
  description: "Clear explanations and direct quotes before any repair work begins in your home."
}, {
  title: "Licensed & Insured GA Plumbers",
  description: "Master plumbing background certified to resolve major leaks, backups, and heater failures."
}];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="serviceareacontact" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium">
            <MapPin className="size-3.5 text-accent" />
            Clayton County & South Metro Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Emergency Plumbing Coverage & Fast Local Dispatch
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Jonesboro Emergency Plumber responds directly to active leaks, sewer backups, and broken water heaters across Jonesboro, GA and surrounding communities.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Coverage List & Trust Factors */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <Card className="border border-border shadow-sm">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground font-default">
                      Primary Service Cities
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Plumbing vans staged throughout the area for fast arrival times.
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 text-xs font-medium bg-secondary text-secondary-foreground px-2.5 py-1.5 rounded-md">
                    <Navigation className="size-3.5" />
                    Live Route Tracking
                  </div>
                </div>

                <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-3" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
                once: true
              }}>
                  {coverageLocations.map((loc, i) => <motion.div key={loc.name} data-index={i} variants={itemVariants} className="flex items-center justify-between p-3 rounded-lg bg-card border border-border/80 hover:border-primary/40 transition-colors">
                      <div className="flex items-center gap-2.5">
                        <div className="size-2 rounded-full bg-accent shrink-0" />
                        <div>
                          <span className="text-sm font-semibold text-card-foreground block">
                            {loc.name}
                          </span>
                          <span className="text-xs text-muted-foreground block">
                            ZIP: {loc.zip}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-muted-foreground bg-background px-2 py-1 rounded border border-border">
                        {loc.estTime}
                      </span>
                    </motion.div>)}
                </motion.div>

                <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span>Need help outside these listed zones? Call our dispatch desk to verify immediate unit availability.</span>
                  <Link to="/service-areas/" className="text-primary font-medium inline-flex items-center gap-1 hover:underline">
                    View detailed area map
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Reassurance Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {highlights.map((item, i) => <div key={item.title} data-index={i} className="p-4 rounded-lg bg-card border border-border">
                  <div className="flex items-center gap-2 text-primary mb-2">
                    <CheckCircle2 className="size-4 shrink-0 text-accent" />
                    <span className="font-semibold text-xs uppercase tracking-wider text-card-foreground">
                      {item.title}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Right Column: Direct Emergency Call Box */}
          <div className="lg:col-span-5">
            <Card className="bg-primary text-primary-foreground border-transparent shadow-xl overflow-hidden relative">
              <div className="p-6 sm:p-8 flex flex-col gap-6">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="font-medium bg-accent text-accent-foreground border-transparent">
                    <Clock className="size-3.5" />
                    24/7 Emergency Line
                  </Badge>
                  <span className="text-xs font-medium opacity-80 flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
                    </span>
                    Dispatch On Duty
                  </span>
                </div>

                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold font-default tracking-tight">
                    Have an Active Leak or Flood Right Now?
                  </h3>
                  <p className="mt-2 text-sm text-primary-foreground/85 leading-relaxed">
                    Do not wait for water damage to spread. Speak immediately with an emergency dispatcher who will direct a plumber to your address.
                  </p>
                </div>

                {/* Big Call Button Card */}
                <div className="p-4 rounded-xl bg-primary-foreground/10 border border-primary-foreground/20 flex flex-col items-center text-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-primary-foreground/75 font-semibold">
                    Direct Dispatch Phone
                  </span>
                  <a href="tel:4708479076" className="text-2xl sm:text-3xl font-extrabold tracking-tight text-accent hover:underline flex items-center gap-2">
                    <Phone className="size-6" />
                    (470) 847-9076
                  </a>
                  <span className="text-xs text-primary-foreground/70">
                    Average response time: 30-45 minutes in Jonesboro
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button asChild size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md">
                    <a href="tel:4708479076">
                      <Phone className="size-4" />
                      Call Emergency Dispatch
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                    <Link to="/contact/">
                      Online Request
                    </Link>
                  </Button>
                </div>

                <Separator className="bg-primary-foreground/20" />

                <div className="flex items-start gap-3 text-xs text-primary-foreground/80">
                  <ShieldCheck className="size-5 shrink-0 text-accent" />
                  <p>
                    Licensed plumbers arrive in fully-stocked vans with pipe diagnostic cameras, hydro-jetters, and replacement components for instant fixes.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}